import { useMemo, useState } from 'react';
import { Plus, Wallet } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { ArchivedCollapsibleSection } from '../../components/common/ArchivedCollapsibleSection';
import { EmptyState } from '../../components/common/EmptyState';
import { ExpensesSectionLabel } from '../../components/expenses/ExpensesSectionLabel';
import { InfoTooltip } from '../../components/common/InfoTooltip';
import { AccountRow } from '../../components/accounts/AccountRow';
import { AccountFormSheet } from '../../components/accounts/AccountFormSheet';
import { AccountsSummaryCard } from '../../components/accounts/AccountsSummaryCard';
import { AccountTypeGroup } from '../../components/accounts/AccountTypeGroup';
import { AccountsLoadingSkeleton } from '../../components/accounts/AccountsLoadingSkeleton';
import { MOBILE_PAGE_PADDING_WITH_STICKY_CLASS } from '../../components/layout/mobileLayoutConstants';
import { Button } from '../../components/ui/Button';
import {
  useArchiveAccountMutation,
  useCreateAccountMutation,
  useFinancialAccountsQuery,
  useRestoreAccountMutation,
  useUpdateAccountMutation,
} from '../../features/accounts/queries';
import { useCurrentSnapshotQuery } from '../../features/snapshots/queries';
import { PageContentShell } from '../../components/common/PageContentShell';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { buildAccountsPageModel } from '../../lib/accountsDisplay';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';

function sortAccounts(accounts) {
  return [...accounts].sort((a, b) => {
    if (a.display_order === b.display_order) {
      return a.name.localeCompare(b.name, 'pt');
    }
    return a.display_order - b.display_order;
  });
}

export function Accounts() {
  const { data: accounts, isPending, isFetching } = useFinancialAccountsQuery();
  const accountsShell = useQueryShellState({ data: accounts, isPending, isFetching });
  const accountsList = useMemo(() => accounts ?? [], [accounts]);
  const { data: currentSnapshot } = useCurrentSnapshotQuery();
  const createAccount = useCreateAccountMutation();
  const updateAccount = useUpdateAccountMutation();
  const archiveAccount = useArchiveAccountMutation();
  const restoreAccount = useRestoreAccountMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const activeAccounts = useMemo(
    () => accountsList.filter((account) => account.is_active),
    [accountsList],
  );
  const archivedAccounts = useMemo(
    () => sortAccounts(accountsList.filter((account) => !account.is_active)),
    [accountsList],
  );

  const pageModel = useMemo(
    () =>
      buildAccountsPageModel({
        accounts: accountsList,
        accountSnapshots: currentSnapshot?.account_snapshots || [],
      }),
    [accountsList, currentSnapshot],
  );

  // `.pen` (1280): "grupos" divide-se em duas colunas — col-a com a primeira
  // metade dos grupos, col-b com o resto (não uma grelha CSS row-major).
  const groupColumns = useMemo(() => {
    const half = Math.ceil(pageModel.groups.length / 2);
    return [pageModel.groups.slice(0, half), pageModel.groups.slice(half)];
  }, [pageModel.groups]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setFormOpen(true);
  };

  const onSubmit = async (values) => {
    try {
      if (editing) {
        await updateAccount.mutateAsync({ id: editing.id, payload: values });
        toast.success(pt.accounts.toasts.updated);
      } else {
        await createAccount.mutateAsync(values);
        toast.success(pt.accounts.toasts.created);
      }
      setFormOpen(false);
      setEditing(null);
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.detail ||
            (Array.isArray(err) ? err.join(' ') : null) ||
            JSON.stringify(err);
      toast.error(message);
    }
  };

  const onArchive = async (account) => {
    try {
      await archiveAccount.mutateAsync(account.id);
      toast.success(pt.accounts.toasts.archived.replace('{name}', account.name));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : pt.accounts.toasts.archiveFailed);
    }
  };

  const onRestore = async (account) => {
    try {
      await restoreAccount.mutateAsync(account.id);
      toast.success(pt.accounts.toasts.restored.replace('{name}', account.name));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : pt.accounts.toasts.restoreFailed);
    }
  };

  const archivedCountText =
    archivedAccounts.length === 1
      ? pt.accounts.summary.countOne
      : pt.accounts.summary.countMany.replace('{count}', String(archivedAccounts.length));

  return (
    <div
      className={cn(
        'relative flex w-full min-w-0 flex-col gap-space-4 overflow-x-hidden',
        MOBILE_PAGE_PADDING_WITH_STICKY_CLASS,
      )}
    >
      <header className="hidden md:flex flex-col gap-space-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <h1 className="type-title-page text-foreground">{pt.accounts.title}</h1>
            <InfoTooltip text={pt.accounts.assetTermTooltip} className="h-4 w-4" />
          </div>
          <p className="type-body-sm text-muted-foreground">{pt.accounts.subtitle}</p>
        </div>
        <Button type="button" size="sm" onClick={openCreate} className="shrink-0">
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          {pt.accounts.addNew}
        </Button>
      </header>

      <PageContentShell shells={[accountsShell]} fallback={<AccountsLoadingSkeleton />}>
        {activeAccounts.length === 0 && archivedAccounts.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title={pt.accounts.empty.title}
            description={pt.accounts.empty.subtitle}
            action={
              <Button onClick={openCreate}>
                <Plus className="mr-1 h-4 w-4" aria-hidden />
                {pt.accounts.empty.cta}
              </Button>
            }
          />
        ) : (
          <>
            {activeAccounts.length > 0 ? (
              <>
                <AccountsSummaryCard
                  totalEur={pageModel.totalEur}
                  liquidEur={pageModel.liquidEur}
                  investedEur={pageModel.investedEur}
                  liquidPct={pageModel.liquidPct}
                  investedPct={pageModel.investedPct}
                  horizonBuckets={pageModel.horizonBuckets}
                />

                <ExpensesSectionLabel
                  title={pt.accounts.listSection}
                  hint={pt.accounts.listSectionSubtitle}
                  hintAlways
                />

                <div className="flex flex-col gap-space-3 wide:flex-row wide:items-start wide:gap-space-5">
                  {groupColumns
                    .filter((column) => column.length > 0)
                    .map((column, columnIndex) => (
                      <div key={columnIndex} className="flex flex-1 flex-col gap-space-3">
                        {column.map((group) => (
                          <AccountTypeGroup
                            key={group.id}
                            group={group}
                            onEdit={openEdit}
                            onArchive={onArchive}
                          />
                        ))}
                      </div>
                    ))}
                </div>
              </>
            ) : (
              <p className="type-body-sm text-muted-foreground">{pt.accounts.allArchivedHint}</p>
            )}

            <ArchivedCollapsibleSection
              title={pt.common.archivedSection.title}
              count={archivedAccounts.length}
              countText={archivedCountText}
              className="overflow-hidden"
            >
              <ul className="flex flex-col gap-space-2">
                {archivedAccounts.map((account) => (
                  <li key={account.id} className="list-none">
                    <AccountRow
                      account={account}
                      dimmed
                      onEdit={openEdit}
                      onRestore={onRestore}
                    />
                  </li>
                ))}
              </ul>
            </ArchivedCollapsibleSection>
          </>
        )}
      </PageContentShell>

      <AccountFormSheet
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={onSubmit}
        initialValues={editing}
        isPending={createAccount.isPending || updateAccount.isPending}
      />
    </div>
  );
}

export default Accounts;
